import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from 'src/components/common/PageHeader';
import { useForm, Controller } from 'react-hook-form';
import { Button } from 'src/components/ui/button';
import StickyFooter from 'src/components/ui/sticky-footer';
import {
  AlertCircle,
  CheckCircle,
  Briefcase,
  GraduationCap,
  Zap,
  Award,
  ClipboardList,
} from 'lucide-react';
import Section from 'src/components/ui/section';
import AppSelect from 'src/components/common/AppSelect';
import { TagSelector } from 'src/components/common/TagSelector';
import { usePersonaInfo } from 'src/services/api/queries/personaQueries';
import {
  useCreatePersona,
  useUpdatePersona,
} from 'src/services/api/mutations/personaMutations';
import { useReferenceData } from 'src/services/api/queries/referenceDataQueries';
import { useTestCollections } from 'src/services/api/queries/testQueries';
import { QualificationGroups } from 'src/components/common/QualificationGroups';
import MarkdownEditor from 'src/components/common/MarkdownEditor';
import { useToast } from 'src/hooks/use-toast';

const FormField = ({
  id,
  label,
  children,
  className = '',
  required = false,
}) => (
  <div className={className}>
    <label
      htmlFor={id}
      className="block text-sm font-semibold leading-6 text-foreground-strong"
    >
      {label}
      {required && <span className="text-destructive ml-1">*</span>}
    </label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const EDUCATION_LEVEL_OPTIONS = [
  { value: 'DEGREE', label: 'Degree' },
  { value: 'MASTERS', label: 'Masters' },
];

// Shared input-group styling (prefix/suffix affixes) for salary & experience.
const inputGroupCls =
  'flex items-stretch rounded-lg border border-input bg-card overflow-hidden transition-[border-color,box-shadow] focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/[0.08]';
const inputBareCls =
  'flex-1 min-w-0 px-3 py-2 bg-transparent text-sm text-foreground-strong placeholder:text-muted-foreground outline-none';
const inputPrefixCls =
  'px-3 flex items-center text-sm font-medium text-muted-foreground bg-muted border-r border-border select-none';
const inputSuffixCls =
  'px-3 flex items-center text-sm font-medium text-muted-foreground bg-muted border-l border-border select-none';
const fieldHintCls = 'text-[11.5px] text-muted-foreground mt-1.5';

const CreatePersona = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const [isEditMode, setIsEditMode] = useState(false);

  // React Query hooks
  const { data: personaData, isLoading: isLoadingPersona } = usePersonaInfo(id);
  const { data: refData } = useReferenceData({
    include: ['career_level', 'skills', 'certifications', 'fields_of_study'],
  });
  const { data: testCollectionsData } = useTestCollections({
    pageNo: 1,
    pageSize: 100,
    status: ['ACTIVE', 'DRAFT'],
  });
  const createPersonaMutation = useCreatePersona();
  const updatePersonaMutation = useUpdatePersona();

  const testCollectionOptions = (testCollectionsData?.data?.items || []).map(
    (item) => ({ value: String(item.id), label: item.name })
  );

  // Reference-data driven option lists.
  const refD = refData?.data || {};
  // Career level: submit the enum id (e.g. MID_LEVEL).
  const careerLevelOptions = (refD.career_level || []).map((item) => ({
    value: item.id,
    label: item.value,
  }));
  const skillOptions = (refD.skills || []).map((item) => ({
    value: String(item.id),
    label: item.name,
  }));
  const certificationOptions = (refD.certifications || []).map((item) => ({
    value: String(item.id),
    label: item.name,
  }));
  // fields_of_study is grouped by category; flatten the nested fields into a
  // single specialty list (field_of_study_id = the nested field id).
  const fieldOfStudyOptions = (refD.fields_of_study || [])
    .flatMap((cat) => cat.fields || [])
    .map((f) => ({ value: String(f.id), label: f.name }));

  const form = useForm({
    defaultValues: {
      title: '',
      persona_description: '',
      career_level: '',
      minimum_salary: '',
      maximum_salary: '',
      min_work_experience: '',
      max_work_experience: '',
      test_collection_id: '',
      detailed_job_description: '',
    },
    mode: 'onChange',
  });

  // Grouped eligibility + chip lists live outside RHF; merged into the payload
  // on submit and hydrated from the persona in edit mode.
  const [qualGroups, setQualGroups] = useState([]);
  const [skillItems, setSkillItems] = useState([]);
  const [certItems, setCertItems] = useState([]);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    trigger,
    watch,
  } = form;

  // Update useEffect to handle edit mode
  useEffect(() => {
    if (id) {
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
      // Ensure form is reset to default values when creating new persona
      reset({
        title: '',
        persona_description: '',
        career_level: '',
        minimum_salary: '',
        maximum_salary: '',
        min_work_experience: '',
        max_work_experience: '',
        test_collection_id: '',
        detailed_job_description: '',
      });
      setQualGroups([]);
      setSkillItems([]);
      setCertItems([]);
    }
  }, [id, reset]);

  // Effect to handle persona data when in edit mode
  // Wait for both persona data and master data to be loaded before resetting form
  // Also ensure masterData state has been updated with options
  useEffect(() => {
    if (isEditMode && personaData?.data && careerLevelOptions.length > 0) {
      const persona = personaData.data;

      try {
        const formData = {
          title: persona.title || '',
          persona_description: persona.persona_description || '',
          minimum_salary: persona.minimum_salary?.toString() || '',
          maximum_salary: persona.maximum_salary?.toString() || '',
          // GET returns the label ("Senior Level"); the select works off the
          // enum id, so map back to the id for display.
          career_level:
            careerLevelOptions.find((o) => o.label === persona.career_level)
              ?.value ||
            persona.career_level ||
            '',
          min_work_experience: persona.min_work_experience?.toString() || '',
          max_work_experience: persona.max_work_experience?.toString() || '',
          test_collection_id: persona.test_collection_id?.toString() || '',
          detailed_job_description: persona.detailed_job_description || '',
        };

        reset(formData);

        // Chip lists hydrate from the persona (kept outside RHF). Skip null
        // entries the API sometimes returns (skill_id/certification_id null).
        setSkillItems(
          Array.isArray(persona.skills)
            ? persona.skills
                .filter((skill) => (skill.skill_id ?? skill.id) != null)
                .map((skill) => ({
                  value: (skill.skill_id ?? skill.id).toString(),
                  isMandatory: Boolean(skill.is_mandatory),
                }))
            : []
        );
        setCertItems(
          Array.isArray(persona.certifications)
            ? persona.certifications
                .filter((cert) => (cert.certification_id ?? cert.id) != null)
                .map((cert) => ({
                  value: (cert.certification_id ?? cert.id).toString(),
                  isMandatory: Boolean(cert.is_mandatory),
                }))
            : []
        );

        // Hydrate the grouped eligibility builder from qualification_requirements.
        setQualGroups(
          Array.isArray(persona.qualification_requirements)
            ? persona.qualification_requirements.map((g) => ({
                is_mandatory: Boolean(g.is_mandatory),
                options: (g.options || []).map((o) => ({
                  qualification_type: o.qualification_type || '',
                  field_of_study_id:
                    o.field_of_study_id != null
                      ? String(o.field_of_study_id)
                      : '',
                  match_any_field: Boolean(o.match_any_field),
                })),
                draftLevel: '',
                draftField: '',
              }))
            : []
        );
      } catch (error) {
        console.error('Error resetting form data:', error);
        console.error('Persona data that caused error:', persona);
      }
    }
  }, [isEditMode, personaData, refData, reset]);

  const numOrNull = (v) =>
    v === '' || v === null || v === undefined ? null : Number(v);

  // Skills / certifications / qualification groups in payload shape (also used
  // to normalize the original persona for the update diff).
  const mapSkills = (items) =>
    items
      .filter((s) => (s.skill_id ?? s.value) != null && s.value !== '')
      .map((s) => ({
        skill_id: Number(s.skill_id ?? s.value),
        is_mandatory: Boolean(s.is_mandatory ?? s.isMandatory),
      }));
  const mapCerts = (items) =>
    items
      .filter((c) => (c.certification_id ?? c.value) != null && c.value !== '')
      .map((c) => ({
        certification_id: Number(c.certification_id ?? c.value),
        is_mandatory: Boolean(c.is_mandatory ?? c.isMandatory),
      }));
  const mapQuals = (groups) =>
    groups
      .filter((g) => (g.options || []).length > 0)
      .map((g, index) => ({
        label: null,
        is_mandatory: Boolean(g.is_mandatory),
        sort_order: index,
        options: g.options.map((o) =>
          o.match_any_field
            ? {
                qualification_type: o.qualification_type,
                match_any_field: true,
              }
            : {
                qualification_type: o.qualification_type,
                field_of_study_id: Number(o.field_of_study_id),
                match_any_field: false,
              }
        ),
      }));

  const onSubmit = async (formData) => {
    try {
      const fullPayload = {
        title: formData.title,
        persona_description: formData.persona_description,
        minimum_salary: numOrNull(formData.minimum_salary),
        maximum_salary: numOrNull(formData.maximum_salary),
        min_work_experience: numOrNull(formData.min_work_experience),
        max_work_experience: numOrNull(formData.max_work_experience),
        detailed_job_description: formData.detailed_job_description || '',
        test_collection_id: numOrNull(formData.test_collection_id),
        // API expects the label ("Mid Level"), though GET returns the enum id.
        career_level:
          careerLevelOptions.find((o) => o.value === formData.career_level)
            ?.label ||
          formData.career_level ||
          null,
        skills: mapSkills(skillItems),
        certifications: mapCerts(certItems),
        qualification_requirements: mapQuals(qualGroups),
      };

      if (isEditMode) {
        // Only send keys that actually changed vs the persona we loaded.
        const persona = personaData?.data || {};
        const eq = (a, b) => JSON.stringify(a) === JSON.stringify(b);
        const original = {
          title: persona.title ?? '',
          persona_description: persona.persona_description ?? '',
          minimum_salary: persona.minimum_salary ?? null,
          maximum_salary: persona.maximum_salary ?? null,
          min_work_experience: persona.min_work_experience ?? null,
          max_work_experience: persona.max_work_experience ?? null,
          detailed_job_description: persona.detailed_job_description ?? '',
          test_collection_id: persona.test_collection_id ?? null,
          skills: mapSkills(persona.skills || []),
          certifications: mapCerts(persona.certifications || []),
          qualification_requirements: mapQuals(
            (persona.qualification_requirements || []).map((g) => ({
              is_mandatory: g.is_mandatory,
              options: g.options || [],
            }))
          ),
        };

        const patch = {};
        Object.keys(original).forEach((k) => {
          if (!eq(fullPayload[k], original[k])) patch[k] = fullPayload[k];
        });
        // career_level: GET returns the label, and fullPayload also carries the
        // label — compare those directly so an unchanged value isn't resent.
        if (fullPayload.career_level !== (persona.career_level ?? null)) {
          patch.career_level = fullPayload.career_level;
        }
        // Backend requires the title on every PUT, so always include it.
        patch.title = fullPayload.title;

        await updatePersonaMutation.mutateAsync({ id, data: patch });
      } else {
        await createPersonaMutation.mutateAsync(fullPayload);
      }

      toast({
        title: 'Success',
        description: `Persona ${isEditMode ? 'updated' : 'created'} successfully`,
      });
      navigate('/personas');
    } catch (error) {
      console.error('Persona operation error:', error);
      toast({
        title: 'Error',
        description: `Failed to ${isEditMode ? 'update' : 'create'} persona. Please try again.`,
        variant: 'destructive',
      });
    }
  };

  const isLoading =
    isLoadingPersona ||
    createPersonaMutation.isPending ||
    updatePersonaMutation.isPending;

  // Money helpers: keep raw digits in form, show commas in UI
  const formatCurrency = (val) => {
    if (val === undefined || val === null || val === '') return '';
    const digits = String(val).replace(/[^0-9]/g, '');
    if (!digits) return '';
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  const parseCurrency = (val) =>
    val ? String(val).replace(/[^0-9]/g, '') : '';

  // Validation logic to count missing required fields (mirror NewRequirement behavior)
  const getMissingFieldsCount = (formValues) => {
    let missingCount = 0;

    // Basic required fields
    // Job Title and Persona Description are mandatory.
    if (!formValues.title) missingCount += 1;
    if (!formValues.persona_description) missingCount += 1;

    // Work experience (min/max), education requirements, skills and
    // certifications are all optional here — added via chips, never partial.

    return missingCount;
  };

  const watchedValues = watch();
  const missingFieldsCount = getMissingFieldsCount(watchedValues);

  // One-line summaries shown in each section header when collapsed.
  const experienceSummary = (() => {
    const mn = watchedValues.min_work_experience;
    const mx = watchedValues.max_work_experience;
    if (mn && mx) return `${mn}–${mx} yrs`;
    if (mn) return `${mn}+ yrs`;
    if (mx) return `≤${mx} yrs`;
    return '';
  })();
  const salarySummary = (() => {
    const mn = watchedValues.minimum_salary;
    const mx = watchedValues.maximum_salary;
    if (mn && mx) return `₹${formatCurrency(mn)}–${formatCurrency(mx)}`;
    if (mn) return `₹${formatCurrency(mn)}+`;
    return '';
  })();
  const eduCount = qualGroups.filter((g) => g.options.length > 0).length;
  const plural = (n, w) => `${n} ${w}${n > 1 ? 's' : ''}`;
  const careerLevelLabel =
    careerLevelOptions.find((o) => o.value === watchedValues.career_level)
      ?.label || '';
  const sectionSummary = {
    jobRequirements: '',
    eligibility: [
      careerLevelLabel,
      salarySummary,
      experienceSummary,
      eduCount ? plural(eduCount, 'edu req') : '',
    ]
      .filter(Boolean)
      .join(' · '),
    skills: skillItems.length ? plural(skillItems.length, 'skill') : '',
    certs: certItems.length ? plural(certItems.length, 'cert') : '',
    test:
      testCollectionOptions.find(
        (o) => o.value === watchedValues.test_collection_id
      )?.label || '',
  };

  const handleNextClick = () => {
    if (missingFieldsCount === 0) {
      handleSubmit(onSubmit)();
    } else {
      trigger();
    }
  };

  return (
    <div className="text-left">
      <PageHeader
        breadcrumbs={[
          { label: 'Job Personas', href: '/personas' },
          {
            label: isEditMode ? 'Edit Persona' : 'Create Persona',
            href: '#',
          },
        ]}
        actions={
          <Button variant="outline" size="headerSm" onClick={() => reset()}>
            clear draft
          </Button>
        }
      />

      <div className="items-center justify-center py-6 px-6 md:px-12 lg:px-20 mt-0 bg-muted pb-24">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="text-left w-full max-w-5xl mx-auto space-y-6"
        >
          <Section
            title="Job Requirements"
            collapsible
            icon={<Briefcase className="w-[18px] h-[18px]" strokeWidth={1.7} />}
            summary={sectionSummary.jobRequirements}
          >
            {/* Job Title */}
            <FormField
              id="title"
              label="Job Title"
              error={errors.title}
              required
              className="w-full mb-4"
            >
              <Controller
                name="title"
                control={control}
                rules={{ required: 'Job Title is required' }}
                render={({ field: { onChange, value } }) => (
                  <input
                    type="text"
                    onChange={onChange}
                    value={value}
                    className={`w-full rounded-lg border bg-card px-3.5 py-2 text-sm text-foreground-strong placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:ring-[3px] ${
                      errors.title
                        ? 'border-destructive focus:border-destructive focus:ring-destructive/[0.08]'
                        : 'border-input focus:border-primary focus:ring-primary/[0.08]'
                    }`}
                  />
                )}
              />
            </FormField>
            <div className="flex flex-col space-y-4">
              {/* Persona Description — mandatory, max 600 chars */}
              <FormField
                id="persona_description"
                label="Persona Description"
                error={errors.persona_description}
                required
                className="w-full"
              >
                <Controller
                  name="persona_description"
                  control={control}
                  rules={{
                    required: 'Persona Description is required',
                    maxLength: {
                      value: 600,
                      message:
                        'Persona Description must be 600 characters or less',
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <div>
                      <textarea
                        id="persona_description"
                        rows={3}
                        maxLength={600}
                        onChange={onChange}
                        value={value || ''}
                        placeholder="Short summary of this persona…"
                        className={`w-full rounded-lg border bg-card px-3.5 py-2.5 text-sm leading-relaxed text-foreground-strong placeholder:text-muted-foreground outline-none resize-y transition-[border-color,box-shadow] focus:ring-[3px] ${
                          errors.persona_description
                            ? 'border-destructive focus:border-destructive focus:ring-destructive/[0.08]'
                            : 'border-input focus:border-primary focus:ring-primary/[0.08]'
                        }`}
                      />
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[11.5px] text-destructive">
                          {errors.persona_description?.message || ''}
                        </span>
                        <span className={fieldHintCls}>
                          {(value || '').length}/600
                        </span>
                      </div>
                    </div>
                  )}
                />
              </FormField>
              {/* Job Description — Markdown textarea with Write / Preview toggle */}
              <FormField
                id="detailed_job_description"
                label="Job Description"
                className="w-full"
              >
                <Controller
                  name="detailed_job_description"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <MarkdownEditor
                      value={value || ''}
                      onChange={onChange}
                      title="Job Description"
                      minHeight={300}
                    />
                  )}
                />
              </FormField>
            </div>
          </Section>
          <Section
            title="Eligibility"
            collapsible
            icon={
              <GraduationCap className="w-[18px] h-[18px]" strokeWidth={1.7} />
            }
            summary={sectionSummary.eligibility}
          >
            <FormField
              id="career_level"
              label="Career Level"
              className="w-full mb-4"
            >
              <Controller
                name="career_level"
                control={control}
                render={({ field }) => (
                  <AppSelect
                    options={careerLevelOptions}
                    onValueChange={field.onChange}
                    value={field.value}
                    placeholder="Select Career Level"
                    className="w-full"
                  />
                )}
              />
            </FormField>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <FormField id="minimum_salary" label="Minimum Salary">
                <Controller
                  name="minimum_salary"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <div className={inputGroupCls}>
                        <span className={inputPrefixCls}>₹</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="e.g. 5"
                          value={formatCurrency(value)}
                          onChange={(e) =>
                            onChange(parseCurrency(e.target.value))
                          }
                          className={inputBareCls}
                        />
                        <span className={inputSuffixCls}>LPA</span>
                      </div>
                      <p className={fieldHintCls}>
                        Lakhs per annum, up to 100 LPA
                      </p>
                    </>
                  )}
                />
              </FormField>
              <FormField id="maximum_salary" label="Maximum Salary">
                <Controller
                  name="maximum_salary"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <div className={inputGroupCls}>
                        <span className={inputPrefixCls}>₹</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="e.g. 25"
                          value={formatCurrency(value)}
                          onChange={(e) =>
                            onChange(parseCurrency(e.target.value))
                          }
                          className={inputBareCls}
                        />
                        <span className={inputSuffixCls}>LPA</span>
                      </div>
                      <p className={fieldHintCls}>
                        Must be greater than minimum salary, up to 100 LPA
                      </p>
                    </>
                  )}
                />
              </FormField>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <FormField id="min_work_experience" label="Min Experience">
                <Controller
                  name="min_work_experience"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <div className={inputGroupCls}>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="e.g. 3"
                        value={value}
                        onChange={(e) =>
                          onChange(e.target.value.replace(/[^0-9]/g, ''))
                        }
                        className={inputBareCls}
                      />
                      <span className={inputSuffixCls}>yrs</span>
                    </div>
                  )}
                />
              </FormField>
              <FormField id="max_work_experience" label="Max Experience">
                <Controller
                  name="max_work_experience"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <div className={inputGroupCls}>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="e.g. 8"
                        value={value}
                        onChange={(e) =>
                          onChange(e.target.value.replace(/[^0-9]/g, ''))
                        }
                        className={inputBareCls}
                      />
                      <span className={inputSuffixCls}>yrs</span>
                    </div>
                  )}
                />
              </FormField>
            </div>
            <FormField
              id="qualifications"
              label="Education Requirements"
              className="w-full"
            >
              <QualificationGroups
                value={qualGroups}
                onChange={setQualGroups}
                levelOptions={EDUCATION_LEVEL_OPTIONS}
                fieldOptions={fieldOfStudyOptions}
              />
            </FormField>
          </Section>
          <Section
            title="Skills and Knowledge"
            collapsible
            icon={<Zap className="w-[18px] h-[18px]" strokeWidth={1.7} />}
            summary={sectionSummary.skills}
          >
            <TagSelector
              label="Skills"
              options={skillOptions}
              value={skillItems}
              onChange={setSkillItems}
              placeholder="Select a skill"
            />
          </Section>
          <Section
            title="Professional Certifications"
            collapsible
            icon={<Award className="w-[18px] h-[18px]" strokeWidth={1.7} />}
            summary={sectionSummary.certs}
          >
            <TagSelector
              label="Certifications"
              options={certificationOptions}
              value={certItems}
              onChange={setCertItems}
              placeholder="Select a certification"
            />
          </Section>
          {/* Test Collection Field */}
          <Section
            title="Test"
            collapsible
            icon={
              <ClipboardList className="w-[18px] h-[18px]" strokeWidth={1.7} />
            }
            summary={sectionSummary.test}
          >
            <FormField id="test_collection_id" label="Test Collection">
              <Controller
                name="test_collection_id"
                control={control}
                render={({ field }) => (
                  <AppSelect
                    options={testCollectionOptions}
                    onValueChange={field.onChange}
                    value={field.value}
                    placeholder="Select Test Collection"
                    error={!!errors.test_collection_id}
                    className="w-full"
                  />
                )}
              />
            </FormField>
          </Section>
        </form>
        <StickyFooter>
          <div className="px-6 md:px-12 lg:px-20 py-2.5">
            <div className="flex items-center justify-between max-w-5xl mx-auto w-full">
              <div className="flex items-center gap-4">
                {missingFieldsCount > 0 && (
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-ds-warning" />
                    <span className="text-sm text-muted-foreground">
                      {missingFieldsCount} required field
                      {missingFieldsCount !== 1 ? 's' : ''} missing
                    </span>
                  </div>
                )}
                {missingFieldsCount === 0 && (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-ds-success" />
                    <span className="text-sm text-muted-foreground">
                      Ready to submit
                    </span>
                  </div>
                )}
              </div>
              <Button
                size="sm"
                onClick={handleNextClick}
                disabled={isLoading}
                className={`text-xs px-6 py-2 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {(() => {
                  if (isLoading) {
                    return isEditMode
                      ? 'Updating Persona...'
                      : 'Creating Persona...';
                  }
                  if (missingFieldsCount === 0) {
                    return 'Confirm & Submit';
                  }
                  return 'Next';
                })()}
              </Button>
            </div>
          </div>
        </StickyFooter>
      </div>
    </div>
  );
};

export default CreatePersona;
